import React, { useState, useEffect } from 'react'
import DataTable from 'react-data-table-component'
import { getStudentdData } from '../../lib/scheduledaysview'
import { useAuth } from '../../_helper/Auth/AuthProvider'

const ScheduleList = ({ studentId }) => {
	const [scheduleList, setScheduleList] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [isDarkMode, setIsDarkMode] = useState(false)
	const [expandedRows, setExpandedRows] = useState({})

	const { user } = useAuth()
	const effectiveStudentId = studentId || user?.id

	const getData = async () => {
		try {
			setLoading(true)
			setError('')
			const schlist = await getStudentdData(effectiveStudentId)
			setScheduleList(schlist)
		} catch (error) {
			console.error('Error fetching schedule data:', error)
			setError('Failed to load schedule data')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		if (effectiveStudentId) {
			getData()
		}
	}, [effectiveStudentId])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		})
		return () => observer.disconnect()
	}, [])


	const getStatusBadge = (status) => {
		if (status === 'Absent') {
			return (
				<span
					className="badge"
					style={{
						backgroundColor: isDarkMode ? 'rgba(220, 53, 69, 0.2)' : 'rgba(220, 53, 69, 0.1)',
						color: isDarkMode ? '#ff6b7a' : '#dc3545',
						border: `1px solid ${isDarkMode ? 'rgba(220, 53, 69, 0.5)' : 'rgba(220, 53, 69, 0.3)'}`,
					}}
				>
					Absent
				</span>
			)
		} else if (status === 'Late') {
			return (
				<span
					className="badge"
					style={{
						backgroundColor: isDarkMode ? 'rgba(255, 193, 7, 0.2)' : 'rgba(255, 193, 7, 0.1)',
						color: isDarkMode ? '#ffd54f' : '#ffc107',
						border: `1px solid ${isDarkMode ? 'rgba(255, 193, 7, 0.5)' : 'rgba(255, 193, 7, 0.3)'}`,
					}}
				>
					Late
				</span>
			)
		} else if (status === 'Present') {
			return (
				<span
					className="badge"
					style={{
						backgroundColor: isDarkMode ? 'rgba(40, 167, 69, 0.2)' : 'rgba(40, 167, 69, 0.1)',
						color: isDarkMode ? '#4caf50' : '#28a745',
						border: `1px solid ${isDarkMode ? 'rgba(40, 167, 69, 0.5)' : 'rgba(40, 167, 69, 0.3)'}`,
					}}
				>
					Present
				</span>
			)
		} else {
			return (
				<span
					className="badge"
					style={{
						backgroundColor: isDarkMode ? 'rgba(108, 117, 125, 0.2)' : 'rgba(108, 117, 125, 0.1)',
						color: isDarkMode ? '#adb5bd' : '#6c757d',
						border: `1px solid ${isDarkMode ? 'rgba(108, 117, 125, 0.5)' : 'rgba(108, 117, 125, 0.3)'}`,
					}}
				>
					-
				</span>
			)
		}
	}

	const columns = React.useMemo(
		() => [
			{
				name: 'Class',
				selector: (row) => row.class_name || '-',
				sortable: true,
				style: {
					color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
				},
			},
			{
				name: 'Date',
				selector: (row) => row.ldate || '-',
				sortable: true,
				style: {
					color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
				},
			},
			{
				name: 'Time',
				selector: (row) => (row.ltime ? row.ltime.substring(0, 5) : '-'),
				sortable: true,
				style: {
					color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
				},
			},
			{
				name: 'Status',
				cell: (row) => getStatusBadge(row.status),
				sortable: true,
				style: {
					color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
				},
			},
			{
				name: 'Details',
				cell: (row) => {
					const isExpanded = expandedRows[row.id] || false
					return (
						<button
							className="btn btn-outline-info btn-sm"
							type="button"
							onClick={(e) => {
								e.stopPropagation()
								setExpandedRows((prev) => ({
									...prev,
									[row.id]: !prev[row.id],
								}))
							}}
							style={{
								borderColor: isDarkMode ? 'rgba(13, 202, 240, 0.5)' : '#0dcaf0',
								color: isDarkMode ? '#0dcaf0' : '#0dcaf0',
							}}
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								fill="currentColor"
								className="bi bi-chevron-double-down"
								viewBox="0 0 16 16"
								style={{
									transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
									transition: 'transform 0.3s ease',
								}}
							>
								<path
									fillRule="evenodd"
									d="M1.646 6.646a.5.5 0 0 1 .708 0L8 12.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"
								/>
								<path
									fillRule="evenodd"
									d="M1.646 2.646a.5.5 0 0 1 .708 0L8 8.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708"
								/>
							</svg>
						</button>
					)
				},
				ignoreRowClick: true,
				allowOverflow: true,
				button: true,
				width: '100px',
			},
		],
		[isDarkMode, expandedRows]
	)

	const ExpandedComponent = ({ data }) => (
		<div
			className="p-3 rounded border"
			style={{
				backgroundColor: isDarkMode ? '#1d1e26' : '#f8f9fa',
				color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
				borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6',
				margin: '10px',
			}}
		>
			<strong style={{ color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'inherit' }}>Notes:</strong>
			<div className="mt-2">{data.note || 'No additional notes available'}</div>
		</div>
	)

	const customStyles = {
		headCells: {
			style: {
				fontWeight: 'bold',
				fontSize: '14px',
				padding: '12px 8px',
				minHeight: '60px',
				backgroundColor: isDarkMode ? '#2a2d35' : '#fff',
				color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
				borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#dee2e6'}`,
			},
		},
		cells: {
			style: {
				fontSize: '14px',
				padding: '12px 8px',
				backgroundColor: isDarkMode ? '#262932' : '#fff',
				color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
			},
		},
		rows: {
			style: {
				backgroundColor: isDarkMode ? '#262932' : '#fff',
				'&:nth-of-type(odd)': {
					backgroundColor: isDarkMode ? '#1d1e26' : '#f8f9fa',
				},
				'&:hover': {
					backgroundColor: isDarkMode ? '#2a2d35' : '#e9ecef',
				},
			},
		},
		pagination: {
			style: {
				backgroundColor: isDarkMode ? '#262932' : '#fff',
				color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
			},
		},
	}

	return (
		<div
			className="card mb-4 shadow-sm"
			style={{
				borderRadius: '8px',
				backgroundColor: isDarkMode ? '#262932' : '#fff',
			}}
		>
			<div
				className="card-header border-bottom d-flex justify-content-between align-items-center"
				style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}
			>
				<div className="d-flex align-items-center">
					<button
						type="button"
						className="btn btn-link p-0 me-2"
						data-bs-toggle="collapse"
						data-bs-target="#collapseSchedule"
						aria-expanded="false"
						aria-controls="collapseSchedule"
					>
						<i className="fa fa-chevron-down"></i>
					</button>
					<h5
						className="mb-0 d-inline-block"
						style={{
							color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
						}}
					>
						Schedule List
					</h5>
				</div>
			</div>
			<div className="collapse show" id="collapseSchedule">
				<div
					className="card-body"
					style={{ backgroundColor: isDarkMode ? '#262932' : '#fff' }}
				>
					{error && (
						<div
							className="alert alert-danger alert-dismissible fade show"
							role="alert"
							style={{
								backgroundColor: isDarkMode ? 'rgba(220, 53, 69, 0.2)' : '#f8d7da',
								borderColor: isDarkMode ? 'rgba(220, 53, 69, 0.5)' : '#f5c2c7',
								color: isDarkMode ? '#ff6b7a' : '#842029',
							}}
						>
							<strong>Error:</strong> {error}
							<button
								type="button"
								className="btn-close"
								onClick={() => setError('')}
								aria-label="Close"
								style={{
									filter: isDarkMode ? 'invert(1)' : 'none',
								}}
							></button>
						</div>
					)}

					<div className="table-responsive">
						<DataTable
							columns={columns}
							data={scheduleList}
							striped={true}
							center={true}
							pagination
							paginationPerPage={10}
							paginationRowsPerPageOptions={[10, 25, 50, 100]}
							progressPending={loading}
							progressComponent={
								<div className="text-center py-4">
									<div className="spinner-border" role="status">
										<span className="visually-hidden">Loading...</span>
									</div>
									<p
										className="mt-2"
										style={{
											color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
										}}
									>
										Loading schedule data...
									</p>
								</div>
							}
							highlightOnHover={true}
							responsive={true}
							noDataComponent={
								<div
									className="text-center py-4"
									style={{
										color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'inherit',
									}}
								>
									No schedule data found
								</div>
							}
							customStyles={customStyles}
							expandableRows={true}
							expandableRowsComponent={ExpandedComponent}
							expandableRowExpanded={(row) => expandedRows[row.id] || false}
							onRowExpandToggled={(toggled, row) => {
								setExpandedRows((prev) => ({
									...prev,
									[row.id]: toggled,
								}))
							}}
							expandableIcon={{
								expanded: <i className="fa fa-chevron-down"></i>,
								collapsed: <i className="fa fa-chevron-right"></i>,
							}}
							theme={isDarkMode ? 'dark' : 'default'}
						/>
					</div>
				</div>
			</div>
		</div>
	)
}

export { ScheduleList }
